import { useEffect, useState } from "react";
import clsx from "clsx";
import type { SubmissionTouchDirection } from "@edge/shared";
import { ApiError, createTouch } from "../../../api";

interface AddTouchDialogProps {
  submissionId: string;
  lenderName: string;
  onCreated: () => Promise<void> | void;
  onCancel: () => void;
}

const DIRECTION_OPTIONS: ReadonlyArray<{
  key: SubmissionTouchDirection;
  label: string;
}> = [
  { key: "inbound", label: "FROM LENDER" },
  { key: "outbound", label: "TO LENDER" },
  { key: "edge", label: "EDGE NOTE" },
];

function nowLocalDatetime(): string {
  // datetime-local needs YYYY-MM-DDTHH:MM in local time, no seconds, no TZ.
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Log a single touch entry. Direction toggle + body textarea + optional
 * occurred_at (defaults to now).
 */
export function AddTouchDialog({
  submissionId,
  lenderName,
  onCreated,
  onCancel,
}: AddTouchDialogProps) {
  const [direction, setDirection] =
    useState<SubmissionTouchDirection>("inbound");
  const [body, setBody] = useState("");
  const [occurredAt, setOccurredAt] = useState<string>(nowLocalDatetime);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !running) onCancel();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onCancel, running]);

  const canSave = body.trim().length > 0 && !running;

  const handleSave = async () => {
    setError(null);
    setRunning(true);
    try {
      await createTouch(submissionId, {
        direction,
        body: body.trim(),
        occurred_at: new Date(occurredAt).toISOString(),
      });
      await onCreated();
    } catch (err) {
      setError(
        err instanceof ApiError
          ? `${err.code} (HTTP ${err.status})`
          : err instanceof Error
            ? err.message
            : "save_failed",
      );
      setRunning(false);
    }
  };

  return (
    <div className="write-confirm-backdrop" role="dialog" aria-modal="true">
      <div className="write-confirm-card submission-touch-card">
        <div className="write-confirm-title">Log touch — {lenderName}</div>

        <div className="submission-form-field">
          <span className="submission-form-label">Direction</span>
          <div className="submission-direction-toggle" role="group">
            {DIRECTION_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                type="button"
                className={clsx(
                  "submission-direction-btn",
                  `dir-${opt.key}`,
                  direction === opt.key && "active",
                )}
                onClick={() => setDirection(opt.key)}
                aria-pressed={direction === opt.key}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <label className="submission-form-field">
          <span className="submission-form-label">
            Body <span className="submission-form-required">·required</span>
          </span>
          <textarea
            className="submission-form-textarea"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={5}
            placeholder="What was said / sent / observed?"
            autoFocus
          />
        </label>

        <label className="submission-form-field">
          <span className="submission-form-label">When</span>
          <input
            type="datetime-local"
            className="submission-form-input"
            value={occurredAt}
            onChange={(e) => setOccurredAt(e.target.value)}
          />
        </label>

        {error && <div className="write-confirm-error">⚠ {error}</div>}

        <div className="write-confirm-actions">
          <button
            className="write-confirm-cancel"
            onClick={onCancel}
            disabled={running}
          >
            CANCEL
          </button>
          <button
            className="write-confirm-confirm"
            onClick={handleSave}
            disabled={!canSave}
          >
            {running ? "WRITING…" : "LOG TOUCH"}
          </button>
        </div>
      </div>
    </div>
  );
}
